/**
 * 飞书 H5 JSSDK 动态加载 + 名字空间探测 + 接口适配。
 *
 * 飞书在不同客户端 / 历史版本下 SDK 暴露的全局对象有差异：
 *   - 新：`window.h5sdk.biz.*`、`window.h5sdk.device.*`、`window.h5sdk.ui.*`
 *   - 中：`window.tt.*`（兼容 Lark 桌面端较早版本）
 *   - 旧：`window.lark.*`
 * 我们在 load 后做一次 capability 探测，统一成这个模块对外的 API。
 *
 * 真机调试时，如果方法命中的不是预期的命名空间，控制台会打印 `[jssdk] picked ...`
 * 便于定位。
 */

const SDK_SRC = "https://lf1-cdn-tos.bytegoofy.com/goofy/lark/op/h5-js-sdk-1.5.35.js";

export interface CurrentChatInfo {
  chat_id: string;
  chat_type: "group" | "p2p" | "unknown";
  thread_id?: string;
}

// 一次 load；重复调用复用 Promise
let loadPromise: Promise<void> | null = null;

function loadSdk(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("not in browser"));
      return;
    }
    // 已经加载过（可能由宿主页面注入）
    if (detectNamespace()) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = SDK_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("failed to load feishu h5 sdk"));
    document.head.appendChild(s);
  });
  return loadPromise;
}

type NS = "h5sdk" | "tt" | "lark";

function detectNamespace(): NS | null {
  const w = window as unknown as Record<string, unknown>;
  if (w.h5sdk) return "h5sdk";
  if (w.tt) return "tt";
  if (w.lark) return "lark";
  return null;
}

function getNs(): Record<string, unknown> | null {
  const name = detectNamespace();
  if (!name) return null;
  return (window as unknown as Record<string, Record<string, unknown>>)[name];
}

/**
 * 拿当前 webview 所在会话的 chat_id / chat_type。
 *
 * - 优先 URL query 的 `chat_id` / `chatId`（AppLink / "+"菜单注入）。
 * - 其次走 JSSDK `getShareInfo` / `getContext`（不同版本 API 不同，依次尝试）。
 * - 都拿不到时返回 null；前端应提示用户"此 webview 未注入 chat 上下文"。
 */
export async function getCurrentChat(): Promise<CurrentChatInfo | null> {
  const fromUrl = readChatFromUrl();
  if (fromUrl) return fromUrl;

  try {
    await loadSdk();
  } catch (e) {
    console.warn("[jssdk] load failed:", e);
    return null;
  }
  const ns = getNs();
  if (!ns) {
    console.warn("[jssdk] no known namespace on window");
    return null;
  }

  const candidates: Array<() => Promise<unknown>> = [
    () => callIfExists(ns, ["biz", "util", "getShareInfo"]),
    () => callIfExists(ns, ["biz", "contact", "getChatInfo"]),
    () => callIfExists(ns, ["getShareInfo"]),
    () => callIfExists(ns, ["getContext"]),
  ];

  for (const c of candidates) {
    try {
      const data = await c();
      const info = normalizeChatInfo(data);
      if (info) {
        console.info("[jssdk] picked chat:", info);
        return info;
      }
    } catch {
      // 继续尝试下一个
    }
  }
  return null;
}

function readChatFromUrl(): CurrentChatInfo | null {
  const p = new URLSearchParams(window.location.search);
  const id = p.get("chat_id") ?? p.get("chatId");
  if (!id) return null;
  const t = p.get("chat_type");
  const chat_type =
    t === "group" || t === "p2p" ? t : "unknown";
  return { chat_id: id, chat_type };
}

function callIfExists(
  root: Record<string, unknown>,
  path: string[],
  args: Record<string, unknown> = {},
): Promise<unknown> {
  let cur: unknown = root;
  for (const seg of path) {
    if (!cur || typeof cur !== "object") {
      return Promise.reject(new Error(`path ${path.join(".")} not found`));
    }
    cur = (cur as Record<string, unknown>)[seg];
  }
  if (typeof cur !== "function") {
    return Promise.reject(new Error(`${path.join(".")} not callable`));
  }
  const fn = cur as (args?: unknown) => unknown;

  return new Promise((resolve, reject) => {
    const maybePromise = fn({
      ...args,
      success: (r: unknown) => resolve(r),
      fail: (e: unknown) => reject(e),
    });
    // 有些实现直接返 Promise
    if (
      maybePromise &&
      typeof (maybePromise as { then?: unknown }).then === "function"
    ) {
      (maybePromise as Promise<unknown>).then(resolve, reject);
    }
  });
}

function normalizeChatInfo(raw: unknown): CurrentChatInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const id =
    (obj.chat_id as string | undefined) ??
    (obj.chatId as string | undefined) ??
    (typeof obj.data === "object" && obj.data
      ? (obj.data as Record<string, unknown>).chat_id as string | undefined
      : undefined);
  if (!id || typeof id !== "string") return null;
  const t = (obj.chat_type as string | undefined) ?? "unknown";
  const chat_type: CurrentChatInfo["chat_type"] =
    t === "group" || t === "p2p" ? (t as "group" | "p2p") : "unknown";
  return { chat_id: id, chat_type };
}


export interface PickedContact {
  open_id: string;
  name: string | null;
  avatar_url?: string | null;
}

/**
 * 唤起飞书选人面板，返回被选中的用户（多选）。
 *
 * 飞书在不同 SDK 版本下方法位置略有差异，依次尝试：
 *   - `h5sdk.biz.contact.chooseChatContact`
 *   - `h5sdk.biz.contact.chooseContact`
 *   - `tt.chooseContact`
 *   - `lark.chooseContact`
 * 所有都取不到 → 返回空数组（前端应显示"请在飞书客户端内使用"提示）。
 */
export async function chooseContact(
  opts: { multi?: boolean; limit?: number } = {},
): Promise<PickedContact[]> {
  try {
    await initH5Sdk();
  } catch {
    return [];
  }

  // 官方文档指定调用方式：tt.chooseContact({ multi, success, fail })
  // https://open.feishu.cn/document/client-docs/gadget/-web-app-api/open-ability/contact/choosecontact
  const tt = (window as any).tt;
  if (tt && typeof tt.chooseContact === "function") {
    try {
      const data = await new Promise<unknown>((resolve, reject) => {
        tt.chooseContact({
          multi: opts.multi ?? true,
          maxNum: opts.limit ?? 50,
          ignore: false,
          success: (res: unknown) => resolve(res),
          fail: (err: unknown) => reject(err),
        });
      });
      const list = normalizePicked(data);
      console.info("[jssdk] chooseContact via tt, picked:", list.length);
      return list;
    } catch (e) {
      console.warn("[jssdk] tt.chooseContact failed:", e);
    }
  }

  // fallback：依次尝试旧版命名空间路径
  const ns = getNs();
  if (!ns) return [];

  const paths: string[][] = [
    ["biz", "contact", "chooseContact"],
    ["chooseContact"],
  ];

  for (const p of paths) {
    try {
      const data = await callIfExists(ns, p, {
        multi: opts.multi ?? true,
        maxNum: opts.limit ?? 50,
      });
      const list = normalizePicked(data);
      if (list.length > 0 || data) {
        console.info("[jssdk] chooseContact picked via", p.join("."));
        return list;
      }
    } catch {
      // 继续尝试下一个
    }
  }
  return [];
}

function normalizePicked(raw: unknown): PickedContact[] {
  if (!raw) return [];
  // 不同 SDK 版本返回形状：
  //   官方 tt.chooseContact: `{ data: [...], department_data: [] }`
  //   旧版: `{users: [...]}`, `{contacts: [...]}`, 直接 `[...]`
  const root: Record<string, unknown> =
    typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const arr =
    (Array.isArray(raw) ? (raw as unknown[]) : null) ??
    (root.data as unknown[] | undefined) ??
    (root.users as unknown[] | undefined) ??
    (root.contacts as unknown[] | undefined) ??
    [];
  const out: PickedContact[] = [];
  for (const u of arr) {
    if (!u || typeof u !== "object") continue;
    const o = u as Record<string, unknown>;
    const open_id = (o.open_id as string | undefined) ?? (o.openId as string | undefined);
    if (!open_id) continue;
    // avatarUrls 是字符串数组，取第一个；兼容旧版 avatar_url / avatar
    const avatarUrls = o.avatarUrls as string[] | undefined;
    out.push({
      open_id,
      name: (o.name as string | null | undefined) ?? null,
      avatar_url:
        (avatarUrls?.[0] as string | null | undefined) ??
        (o.avatar_url as string | null | undefined) ??
        (o.avatar as string | null | undefined) ??
        null,
    });
  }
  return out;
}

// ---- 消息快捷操作：通过 triggerCode 获取消息原文 ---------------------------

export interface MessageActionDetail {
  support: boolean;
  content: string;
  msgType: string;
  messages?: Array<{
    messageId: string;
    content: string;
    msgType: string;
    sender?: { name?: string; open_id?: string };
    openChatId?: string;
    openMessageId?: string;
    createTime?: number;
    status?: boolean;
  }>;
}

/**
 * 通过 triggerCode 获取消息快捷操作选中的消息内容。
 *
 * 文档：https://open.feishu.cn/document/getBlockActionSourceDetail
 * 仅在飞书客户端内、且已完成 h5sdk.config 鉴权时可用。
 */
export async function getBlockActionSourceDetail(
  triggerCode: string,
): Promise<MessageActionDetail> {
  // initH5Sdk 会等 h5sdk.ready 触发后才返回，确保 JSAPI 已可用
  await initH5Sdk();

  // 官方文档示例：tt.getBlockActionSourceDetail({ triggerCode, success, fail })
  const tt = (window as any).tt;
  if (!tt || typeof tt.getBlockActionSourceDetail !== "function") {
    throw new Error("tt.getBlockActionSourceDetail 不可用（非飞书客户端？）");
  }

  const data = await new Promise<unknown>((resolve, reject) => {
    tt.getBlockActionSourceDetail({
      triggerCode,
      success: (res: unknown) => resolve(res),
      fail: (err: unknown) => reject(err),
    });
  });

  console.info("[jssdk] getBlockActionSourceDetail raw data:", JSON.stringify(data, null, 2));
  const result = normalizeMessageActionDetail(data);
  if (!result) {
    throw new Error("getBlockActionSourceDetail 返回数据异常");
  }
  console.info("[jssdk] getBlockActionSourceDetail success");
  return result;
}

function normalizeMessageActionDetail(raw: unknown): MessageActionDetail | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;

  // 飞书返回格式：{ bizType, content: { actionTime, messages: [...] } }
  // content.messages 里每条消息有 messageType / content / sender / openMessageId 等
  const contentObj = obj.content as Record<string, unknown> | undefined;
  const rawMessages = Array.isArray(contentObj?.messages)
    ? (contentObj!.messages as Array<Record<string, unknown>>)
    : Array.isArray(obj.messages)
      ? (obj.messages as Array<Record<string, unknown>>)
      : [];

  const messages = rawMessages.map((m) => ({
    messageId: (m.openMessageId ?? m.messageId ?? m.message_id ?? "") as string,
    content: (m.content ?? "") as string,
    msgType: (m.messageType ?? m.msgType ?? m.msg_type ?? "") as string,
    sender: m.sender
      ? {
          name: ((m.sender as Record<string, unknown>).name as string | undefined) ?? undefined,
          open_id: ((m.sender as Record<string, unknown>).open_id as string | undefined) ?? undefined,
        }
      : undefined,
    openChatId: (m.openChatId as string | undefined) ?? undefined,
    openMessageId: (m.openMessageId as string | undefined) ?? undefined,
    createTime: (m.createTime as number | undefined) ?? undefined,
    status: (m.status as boolean | undefined) ?? undefined,
  }));

  // 用第一条消息的 support 判断整体状态
  const first = rawMessages[0] as Record<string, unknown> | undefined;
  const support = first?.support === true;
  const firstContent = (first?.content as string | undefined) ?? "";
  const msgType = (first?.messageType as string | undefined) ?? "";

  return { support, content: firstContent, msgType, messages };
}

/**
 * 从 URL 的 bdp_launch_query 参数中提取 triggerCode (__trigger_id__)。
 */
export function getTriggerCodeFromUrl(): string | null {
  const raw = new URLSearchParams(window.location.search).get("bdp_launch_query");
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw);
    return (obj.__trigger_id__ as string | undefined) ?? null;
  } catch {
    return null;
  }
}

// ---- 入口场景判断 ----------------------------------------------------------------

export type EntryScenario = "message_action" | "plus_menu" | "direct";

/**
 * 判断当前 webview 的入口场景。
 *
 * - message_action：消息快捷操作（长按消息 → 更多 → 收藏）
 * - plus_menu：+ 号菜单进入（PC: from=plus_menu_*; 移动端: required_launch_ability=chat_action）
 * - direct：直接打开应用（无聊天上下文）
 */
export function detectEntryScenario(): EntryScenario {
  const p = new URLSearchParams(window.location.search);
  const from = p.get("from");
  if (from === "message_action") return "message_action";
  if (from === "plus_menu_p2p" || from === "plus_menu_group") return "plus_menu";
  if (p.get("required_launch_ability") === "chat_action") return "plus_menu";
  return "direct";
}

// ---- + 号菜单：getTriggerContext -------------------------------------------------

/**
 * 通过 triggerCode 获取 + 号菜单的触发上下文（openChatId）。
 *
 * 文档：https://open.feishu.cn/document/web-app/gadget-api/open-ability/chat/gettriggercontext
 * 仅在飞书客户端内、且已完成 h5sdk.config 鉴权时可用。
 */
export async function getTriggerContext(
  triggerCode: string,
): Promise<{ openChatId: string } | null> {
  await initH5Sdk();

  const tt = (window as any).tt;
  if (!tt || typeof tt.getTriggerContext !== "function") {
    console.warn("[jssdk] tt.getTriggerContext 不可用");
    return null;
  }

  try {
    const data = await new Promise<unknown>((resolve, reject) => {
      tt.getTriggerContext({
        triggerCode,
        success: (res: unknown) => resolve(res),
        fail: (err: unknown) => reject(err),
      });
    });

    if (!data || typeof data !== "object") return null;
    const obj = data as Record<string, unknown>;
    const openChatId = (obj.openChatId as string | undefined) ?? null;
    if (!openChatId) return null;
    console.info("[jssdk] getTriggerContext openChatId:", openChatId);
    return { openChatId };
  } catch (e) {
    console.warn("[jssdk] getTriggerContext failed:", e);
    return null;
  }
}

// ---- 直接进入：chooseChat --------------------------------------------------------

/**
 * 唤起飞书会话选择面板，让用户选择一个目标会话。
 *
 * 文档：https://open.feishu.cn/document/client-docs/gadget/-web-app-api/open-ability/chat/choosechat
 * 仅在飞书客户端内可用。
 */
export async function chooseChat(): Promise<{
  chatId: string;
  chatType: string;
} | null> {
  await initH5Sdk();

  const tt = (window as any).tt;
  if (!tt || typeof tt.chooseChat !== "function") {
    console.warn("[jssdk] tt.chooseChat 不可用");
    return null;
  }

  try {
    const data = await new Promise<unknown>((resolve, reject) => {
      tt.chooseChat({
        multiSelect: false,
        selectType: 0,
        confirmTitle: "选择会话",
        confirmText: "发送到此会话",
        success: (res: unknown) => resolve(res),
        fail: (err: unknown) => reject(err),
      });
    });

    if (!data || typeof data !== "object") return null;
    const obj = data as Record<string, unknown>;
    const arr = obj.data as Array<Record<string, unknown>> | undefined;
    if (!arr || arr.length === 0) return null;
    const chatId = arr[0].id as string;
    const chatType = (arr[0].chatType as string) ?? "unknown";
    console.info("[jssdk] chooseChat picked:", chatId);
    return { chatId, chatType };
  } catch (e) {
    console.warn("[jssdk] chooseChat failed:", e);
    return null;
  }
}

// ---- H5 SDK 鉴权 -----------------------------------------------------------------

/**
 * H5 SDK 鉴权：调用后端 /api/h5sdk/config 获取签名参数并注入。
 *
 * 平台差异：
 * - iOS 上 `h5sdk` 命名空间的 config 内部调用 `updateMeta`（iOS 原生层无此方法），
 *   会抛 `this.sdk[e] is not a function`。iOS 必须用 `tt.config`。
 * - Android 上两者都可用。
 * - PC 端优先用 `tt.config`（文档推荐）。
 *
 * 统一策略：先试 `tt.config`，再试 `h5sdk.config`。
 */
export async function initH5Sdk(): Promise<void> {
  await loadSdk();
  const ns = getNs();
  if (!ns) throw new Error("SDK 命名空间未加载（非飞书客户端内？）");

  // 已经 ready 过则跳过（JSAPI 已可用）
  if ((window as unknown as Record<string, unknown>).__h5sdk_configured) return;

  // 预检查：如果 JWT 不存在，先触发 OAuth 登录再继续。
  // 避免 /api/h5sdk/config 的 401 响应触发页面跳转，导致 zustand store 中
  // 已设置的 currentChat 状态丢失。
  try {
    const probe = await fetch("/api/auth/me", { credentials: "include" });
    if (probe.status === 401) {
      const redirect = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/api/auth/login?redirect=${redirect}`;
      return new Promise<void>(() => {}); // 挂起，等页面跳转
    }
  } catch {
    // 网络错误等，继续尝试（让后续 config 请求自然失败）
  }

  // 飞书官方文档推荐：encodeURIComponent(location.href.split('#')[0])
  // 示例中 GET 传输时后端框架会自动 decode；我们用 JSON POST，
  // 后端需手动 decodeURIComponent 恢复原始 URL 再签名。
  const url = encodeURIComponent(window.location.href.split('#')[0]);

  const resp = await fetch("/api/h5sdk/config", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });
  if (!resp.ok) {
    const errText = await resp.text().catch(() => "");
    throw new Error(`h5sdk config HTTP ${resp.status}: ${errText.slice(0, 200)}`);
  }
  const cfg = await resp.json();

  const cfgData = {
    appId: cfg.appId,
    timestamp: cfg.timestamp,
    nonceStr: cfg.nonceStr,
    signature: cfg.signature,
    jsApiList: [
      "getBlockActionSourceDetail",
      "getTriggerContext",
      "chooseChat",
      "chooseContact",
    ],
    onSuccess: (res: unknown) => console.info("[jssdk] config ok:", JSON.stringify(res)),
    onFail: (err: unknown) => console.error("[jssdk] config fail:", JSON.stringify(err)),
  };

  // 先 tt，再 h5sdk
  let configured = false;
  let lastError: string | null = null;

  if (typeof (window as any).tt?.config === "function") {
    try {
      (window as any).tt.config(cfgData);
      configured = true;
      console.info("[jssdk] used tt.config");
    } catch (e) {
      lastError = `tt.config: ${e}`;
      console.warn("[jssdk] tt.config throw:", e);
    }
  }
  if (!configured && typeof (window as any).h5sdk?.config === "function") {
    try {
      (window as any).h5sdk.config(cfgData);
      configured = true;
      console.info("[jssdk] used h5sdk.config");
    } catch (e) {
      lastError = lastError ?? `h5sdk.config: ${e}`;
      console.warn("[jssdk] h5sdk.config throw:", e);
    }
  }

  if (!configured) {
    throw new Error(lastError ?? "h5sdk/tt.config 方法不存在");
  }

  // 关键：等 h5sdk.ready 触发后 JSAPI 才可用
  // 官方示例：h5sdk.ready(() => { tt.getBlockActionSourceDetail(...) })
  await new Promise<void>((resolve) => {
    if ((window as any).h5sdk?.ready) {
      (window as any).h5sdk.ready(() => {
        console.info("[jssdk] h5sdk.ready fired");
        (window as unknown as Record<string, unknown>).__h5sdk_configured = true;
        resolve();
      });
    } else {
      // tt 命名空间没有 ready 方法，config 成功即可
      (window as unknown as Record<string, unknown>).__h5sdk_configured = true;
      resolve();
    }
    // 超时兜底
    setTimeout(() => {
      console.warn("[jssdk] h5sdk.ready timeout");
      (window as unknown as Record<string, unknown>).__h5sdk_configured = true;
      resolve();
    }, 5000);
  });
}
